/**
 * Integration Service
 * Handles NetSuite OAuth 2.0 integration management via the backend API
 */

let INTEGRATIONS_API_URL = ""

async function loadApiUrl(): Promise<string> {
  if (INTEGRATIONS_API_URL) {
    return INTEGRATIONS_API_URL
  }

  try {
    const response = await fetch("/aws-exports.json")
    const config = await response.json()
    const baseUrl = config.feedbackApiUrl
    if (!baseUrl) {
      throw new Error("API URL not found in aws-exports.json")
    }
    INTEGRATIONS_API_URL = `${baseUrl.replace(/\/?$/, "/")}integrations`
    return INTEGRATIONS_API_URL
  } catch (error) {
    console.error("Failed to load API URL from aws-exports.json:", error)
    throw new Error("Integrations API URL not configured")
  }
}

async function fetchWithAuth(url: string, idToken: string, options: RequestInit = {}) {
  const response = await fetch(url, {
    ...options,
    cache: "no-store",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${idToken}`,
      ...options.headers,
    },
  })

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}))
    throw new Error(errorData.error?.message || `HTTP error! status: ${response.status}`)
  }

  return response.json()
}

export interface IntegrationStatus {
  integrationId: string
  status: "connected" | "expired" | "error" | "unknown"
  connectedAt: string
  netsuiteAccountId: string
}

export interface ConnectResponse {
  authorizationUrl: string
  state: string
}

export async function listIntegrations(idToken: string): Promise<IntegrationStatus[]> {
  const apiUrl = await loadApiUrl()
  const data = await fetchWithAuth(apiUrl, idToken)
  return data.integrations || []
}

export async function initiateNetSuiteConnect(
  idToken: string,
  accountId: string
): Promise<ConnectResponse> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/netsuite/connect`, idToken, {
    method: "POST",
    body: JSON.stringify({ accountId }),
  })
}

export async function completeNetSuiteConnect(
  idToken: string,
  code: string,
  state: string
): Promise<{ success: boolean; status: string; connectedAt: string }> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/netsuite/callback`, idToken, {
    method: "POST",
    body: JSON.stringify({ code, state }),
  })
}

export interface IntegrationSettings {
  netsuiteClientId: string
  isConfigured: boolean
}

export async function getIntegrationSettings(idToken: string): Promise<IntegrationSettings> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/settings`, idToken)
}

export async function updateIntegrationSettings(
  idToken: string,
  settings: { netsuiteClientId: string }
): Promise<{ success: boolean }> {
  const apiUrl = await loadApiUrl()
  return fetchWithAuth(`${apiUrl}/settings`, idToken, {
    method: "PUT",
    body: JSON.stringify(settings),
  })
}

export async function disconnectNetSuite(idToken: string): Promise<void> {
  const apiUrl = await loadApiUrl()
  await fetchWithAuth(`${apiUrl}/netsuite`, idToken, {
    method: "DELETE",
  })
}
